import { useState, useEffect, useCallback, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { SHOWCASE_SLIDES } from "../lib/site";
import { scrollToId } from "../hooks/useSmoothScroll";

const HOLD = 4500; // ms each slide stays in front (also the zoom length)
const VISIBLE = 2; // how many slides show on each side of the active one

export const ShowcaseCarousel = () => {
  const slides = SHOWCASE_SLIDES;
  const n = slides.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const stageRef = useRef(null);
  const inView = useInView(stageRef, { margin: "-15% 0px -15% 0px" });
  const navigate = useNavigate();

  const step = (d) => setIndex((i) => (i + d + n) % n);
  const touch = useRef({ x: null, y: null });
  const movedRef = useRef(false);
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    touch.current = { x: t?.clientX ?? null, y: t?.clientY ?? null };
    movedRef.current = false;
  };
  const onTouchMove = (e) => {
    const t = e.touches?.[0];
    if (!t || touch.current.x == null) return;
    if (Math.abs(t.clientX - touch.current.x) > 10 || Math.abs(t.clientY - touch.current.y) > 10) movedRef.current = true;
  };
  const onTouchEnd = (e) => {
    const { x, y } = touch.current;
    const t = e.changedTouches?.[0];
    touch.current = { x: null, y: null };
    if (x == null || !t) return;
    const dx = t.clientX - x;
    const dy = t.clientY - y;
    // Only treat as a slide change when the gesture is clearly horizontal.
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) step(dx < 0 ? 1 : -1);
    // Keep the "moved" flag just long enough to swallow the click this gesture
    // may emit (so a swipe never navigates), then clear it for future taps.
    if (movedRef.current) setTimeout(() => { movedRef.current = false; }, 260);
  };

  const goTo = (link) => {
    if (!link || movedRef.current) return; // ignore taps that were actually swipes
    if (link.startsWith("#")) scrollToId(link.slice(1));
    else navigate(link);
  };

  // Shortest signed distance from active on the ring, so slides orbit both ways.
  const offsetOf = (i) => {
    let o = i - index;
    if (o > n / 2) o -= n;
    if (o < -n / 2) o += n;
    return o;
  };

  // Auto-advance only while the carousel is on screen and not hovered.
  useEffect(() => {
    if (paused || !inView || n <= 1) return;
    const t = setTimeout(() => step(1), HOLD);
    return () => clearTimeout(t);
  }, [index, paused, inView, n]);

  if (!n) return null;

  return (
    <section id="work" className="relative overflow-hidden py-24 lg:py-32 bg-[#0A0C0F]" data-testid="showcase-carousel">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-10 lg:mb-14">
          <p className="mb-5 text-xs font-bold uppercase tracking-[0.3em] text-[#C6FF00]">/ The Showcase</p>
          <h2 className="text-5xl font-black uppercase leading-[0.9] tracking-tighter lg:text-7xl">
            Work that<br /><span className="text-stroke-lime">moves</span>
          </h2>
        </div>

        <motion.div
          ref={stageRef}
          className="relative"
          style={{ perspective: "1800px" }}
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="showcase-stage"
        >
          <div className="relative h-[340px] sm:h-[440px] lg:h-[560px]" style={{ transformStyle: "preserve-3d", touchAction: "pan-y" }} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
            {slides.map((s, i) => {
              const offset = offsetOf(i);
              const abs = Math.abs(offset);
              const active = offset === 0;
              const hidden = abs > VISIBLE;
              return (
                <motion.div
                  key={s.image + i}
                  className="absolute left-1/2 top-1/2 h-full w-[78%] sm:w-[62%] lg:w-[52%]"
                  style={{ zIndex: 100 - abs, transformStyle: "preserve-3d", pointerEvents: active ? "auto" : "none" }}
                  transformTemplate={(l) =>
                    `translateX(-50%) translateY(-50%) translateX(${l.x || "0%"}) translateZ(${l.z || "0px"}) rotateY(${l.rotateY || "0deg"}) scale(${l.scale ?? 1})`
                  }
                  animate={
                    inView
                      ? {
                          x: `${offset * 46}%`,
                          z: `${-abs * 240}px`,
                          rotateY: `${offset * -42}deg`,
                          scale: active ? 1 : 0.82 - (abs - 1) * 0.1,
                          opacity: hidden ? 0 : active ? 1 : abs === 1 ? 0.55 : 0.28,
                        }
                      : { opacity: 0, z: "-500px", scale: 0.6 }
                  }
                  transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div
                    className="relative h-full w-full overflow-hidden border border-[#2A2D34] bg-black shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]"
                    data-testid={active ? "showcase-active-card" : undefined}
                  >
                    <motion.img
                      src={s.image}
                      alt={s.title || "Showcase"}
                      className="h-full w-full object-cover"
                      draggable={false}
                      data-testid={active ? "showcase-image" : undefined}
                      animate={active && inView ? { scale: [1.06, 1.26] } : { scale: 1.06 }}
                      transition={active && inView ? { duration: HOLD / 1000, ease: "linear" } : { duration: 0.6 }}
                    />
                    {/* dim non-active slides + brand gradient on the active one */}
                    <div className={`pointer-events-none absolute inset-0 transition-colors duration-500 ${active ? "bg-gradient-to-t from-[#0A0C0F] via-[#0A0C0F]/15 to-transparent" : "bg-[#0A0C0F]/45"}`} />
                    {active && (s.title || s.subtitle) && (
                      <motion.div
                        key={`cap-${index}`}
                        className="absolute bottom-0 left-0 right-0 flex flex-wrap items-end justify-between gap-4 p-6 sm:p-9"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      >
                        <div className="min-w-0">
                          {s.title && <h3 className="text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{s.title}</h3>}
                          {s.subtitle && <p className="mt-1.5 max-w-md text-sm font-medium text-[#C7CDD4]">{s.subtitle}</p>}
                        </div>
                        {s.link && s.cta && (
                          <button
                            type="button"
                            onClick={() => goTo(s.link)}
                            data-testid="showcase-cta"
                            className="btn-shine group inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#C6FF00] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-[#0A0C0F] transition-colors duration-300 hover:bg-white"
                          >
                            {s.cta} <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </button>
                        )}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              );
            })}

            {/* Arrows */}
            <button onClick={() => step(-1)} aria-label="Previous slide" data-testid="showcase-prev"
              className="absolute left-2 top-1/2 z-[120] hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-sm transition-colors hover:border-[#C6FF00] hover:text-[#C6FF00] sm:left-4 sm:flex">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={() => step(1)} aria-label="Next slide" data-testid="showcase-next"
              className="absolute right-2 top-1/2 z-[120] hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-sm transition-colors hover:border-[#C6FF00] hover:text-[#C6FF00] sm:right-4 sm:flex">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Dots */}
          <div className="mt-6 flex items-center justify-center gap-2">
            {slides.map((s, i) => (
              <button key={s.title} onClick={() => setIndex(i)} aria-label={`Go to slide ${i + 1}`} data-testid={`showcase-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-[#C6FF00]" : "w-1.5 bg-white/40 hover:bg-white/70"}`} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ShowcaseCarousel;
